import { Anchor } from 'src/design/atoms/Anchor';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { IconThumbDownFilled, IconThumbUpFilled } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { AdminFeedbackItemDto } from 'src/api/FiretrolApiClient';
import {
  FEEDBACK_RATING_UP,
  feedbackReasonLabel,
} from 'src/app/chat/messageFeedback';
import { reasoningEffortLabel } from 'src/routes/Settings/AIAssistant/replyUsage';
import { abbreviateString } from 'src/utils';

// A reply can run to thousands of characters; the tooltip shows the start and
// the row click opens the whole transcript.
const TOOLTIP_MAX_CHARS = 800;

const votedAtFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export const formatVotedAt = (value: Date | string | undefined): string => {
  const date = value === undefined ? null : new Date(value);
  return date && !Number.isNaN(date.getTime())
    ? votedAtFormatter.format(date)
    : '';
};

const ClampedText: React.FC<{ text?: string }> = ({ text }) =>
  text ? (
    <Tooltip
      label={abbreviateString(text, TOOLTIP_MAX_CHARS)}
      multiline
      w={420}
      openDelay={300}
      style={{ whiteSpace: 'pre-wrap' }}
    >
      <Text
        size="sm"
        c="bright"
        lineClamp={2}
        style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
      >
        {text}
      </Text>
    </Tooltip>
  ) : null;

const RatingCell: React.FC<{ rating: string }> = ({ rating }) => {
  const isUp = rating === FEEDBACK_RATING_UP;
  return (
    <Group gap={6} wrap="nowrap">
      <Box c={isUp ? 'green.7' : 'red.7'} display="flex">
        {isUp ? (
          <IconThumbUpFilled size={14} aria-hidden />
        ) : (
          <IconThumbDownFilled size={14} aria-hidden />
        )}
      </Box>
      <Text size="sm" c="bright">
        {isUp ? 'Helpful' : 'Not helpful'}
      </Text>
    </Group>
  );
};

export const getFeedbackColumns = (
  onOpen: (item: AdminFeedbackItemDto) => void,
): MRT_ColumnDef<AdminFeedbackItemDto>[] => [
  {
    accessorKey: 'votedAt',
    header: 'Voted',
    size: 150,
    Cell: ({ row }) => (
      <Text size="sm" c="bright">
        {formatVotedAt(row.original.votedAt)}
      </Text>
    ),
  },
  {
    accessorKey: 'userName',
    header: 'User',
    size: 130,
    Cell: ({ row }) => (
      <Text size="sm" c="bright">
        {row.original.userName}
      </Text>
    ),
  },
  {
    accessorKey: 'rating',
    header: 'Rating',
    size: 120,
    Cell: ({ row }) => <RatingCell rating={row.original.rating} />,
  },
  {
    accessorKey: 'reason',
    header: 'Reason',
    size: 135,
    Cell: ({ row }) =>
      row.original.reason ? (
        <Text size="sm" c="bright">
          {feedbackReasonLabel(row.original.reason)}
        </Text>
      ) : null,
  },
  {
    accessorKey: 'comment',
    header: 'Comment',
    size: 160,
    Cell: ({ row }) => <ClampedText text={row.original.comment} />,
  },
  {
    accessorKey: 'question',
    header: 'Question',
    size: 180,
    Cell: ({ row }) => <ClampedText text={row.original.question} />,
  },
  {
    accessorKey: 'answer',
    header: 'Answer',
    size: 230,
    Cell: ({ row }) => <ClampedText text={row.original.answer} />,
  },
  {
    accessorKey: 'reasoningEffort',
    header: 'Effort',
    size: 110,
    Cell: ({ row }) => (
      <Text size="sm" c={row.original.reasoningEffort ? 'bright' : 'dimmed'}>
        {reasoningEffortLabel(row.original.reasoningEffort)}
      </Text>
    ),
  },
  {
    accessorKey: 'conversationTitle',
    header: 'Conversation',
    size: 140,
    Cell: ({ row }) => (
      <Anchor
        component="button"
        type="button"
        size="sm"
        lineClamp={2}
        ta="left"
        aria-label={`Open conversation ${row.original.conversationTitle}`}
        style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          onOpen(row.original);
        }}
      >
        {row.original.conversationTitle}
      </Anchor>
    ),
  },
];
